import { z } from 'zod';
import { achievementIdSchema } from './achievements';
import { type LeaderboardReset, leaderboardIdSchema, leaderboardPeriod } from './leaderboards';
import { manifestIdSchema } from './manifest';

// Live events and the scheduler (M37). An event changes some of a game's config values for a
// set time, once or every day or week, and can count a board's scores on a board of its own
// while it's on. A rule runs by itself: when a board starts fresh, when an event ends, or at
// set times. Whether an event is on is worked out from its times whenever it's asked, so it
// turns on and off on the minute; the worker logs each turn and runs the rules.

const MINUTE_MS = 60_000;
export const DAY_MINUTES = 24 * 60;
export const WEEK_MINUTES = 7 * DAY_MINUTES;
/** Weeks count from a Monday 00:00 UTC (Jan 5, 1970), as leaderboards' weeks do. */
const FIRST_MONDAY = Date.UTC(1970, 0, 5);
/** Longest a one-time event can last. */
const LONGEST_ONCE_MS = 90 * 24 * 60 * MINUTE_MS;

export const EVENT_REPEATS = ['once', 'daily', 'weekly'] as const;
export type EventRepeat = (typeof EVENT_REPEATS)[number];

export const eventIdSchema = manifestIdSchema;

/** A config value's name: letters, digits, `_`, `-` and `.`, up to 64. */
export const CONFIG_KEY_PATTERN = /^[A-Za-z0-9_.-]{1,64}$/;
export const configKeySchema = z
  .string()
  .regex(CONFIG_KEY_PATTERN, 'Use up to 64 letters, digits, dots, dashes or underscores');

/** Config values one event can change. */
export const EVENT_CONFIG_VALUES = 20;

/** When an event is on, as stored. */
export interface EventSchedule {
  repeat: EventRepeat;
  /** Once: when it turns on. Repeating: no run starts before this (none: from now on). */
  startsAt: Date | null;
  /** Once: when it turns off. Repeating: no run starts at or after this (none: never ends). */
  endsAt: Date | null;
  /** Repeating: minutes into the day, or into the week from Monday 00:00 UTC, it turns on. */
  onMinute: number | null;
  offMinute: number | null;
}

/** One time an event is on: from `start` until `end`. */
export interface EventRun {
  start: Date;
  end: Date;
}

const cycleOf = (repeat: EventRepeat) => (repeat === 'daily' ? DAY_MINUTES : WEEK_MINUTES);

/** How many minutes each run of a repeating event lasts (0: never on). */
function runMinutes(schedule: EventSchedule): number {
  if (schedule.onMinute === null || schedule.offMinute === null) return 0;
  const cycle = cycleOf(schedule.repeat);
  return (((schedule.offMinute - schedule.onMinute) % cycle) + cycle) % cycle;
}

/** The repeating run that starts `k` cycles after the first Monday. */
function repeatingRun(schedule: EventSchedule, k: number): EventRun {
  const cycle = cycleOf(schedule.repeat);
  const start = FIRST_MONDAY + (k * cycle + (schedule.onMinute ?? 0)) * MINUTE_MS;
  return { start: new Date(start), end: new Date(start + runMinutes(schedule) * MINUTE_MS) };
}

/** Which cycle's run is the last to start at or before `at`. */
function cycleAt(schedule: EventSchedule, at: number): number {
  const minutes = Math.floor((at - FIRST_MONDAY) / MINUTE_MS);
  return Math.floor((minutes - (schedule.onMinute ?? 0)) / cycleOf(schedule.repeat));
}

/** The run that started last at or before `at` (it may have ended since), or null. */
export function lastRun(schedule: EventSchedule, at: Date): EventRun | null {
  if (schedule.repeat === 'once') {
    if (!schedule.startsAt || !schedule.endsAt || schedule.startsAt > at) return null;
    return { start: schedule.startsAt, end: schedule.endsAt };
  }
  if (!runMinutes(schedule)) return null;
  let time = at.getTime();
  // Nothing starts at or after the last day: count back from just before it.
  if (schedule.endsAt && time >= schedule.endsAt.getTime()) time = schedule.endsAt.getTime() - 1;
  const run = repeatingRun(schedule, cycleAt(schedule, time));
  if (schedule.startsAt && run.start < schedule.startsAt) return null;
  return run;
}

/** The first run that starts after `at`, or null when there's none. */
export function nextRun(schedule: EventSchedule, at: Date): EventRun | null {
  if (schedule.repeat === 'once') {
    if (!schedule.startsAt || !schedule.endsAt || schedule.startsAt <= at) return null;
    return { start: schedule.startsAt, end: schedule.endsAt };
  }
  if (!runMinutes(schedule)) return null;
  let k = cycleAt(schedule, at.getTime()) + 1;
  if (schedule.startsAt) {
    k = Math.max(k, cycleAt(schedule, schedule.startsAt.getTime() - 1) + 1);
  }
  const run = repeatingRun(schedule, k);
  if (schedule.endsAt && run.start >= schedule.endsAt) return null;
  return run;
}

/** The run the event is in at `at`, or null when it's off then. */
export function runAt(schedule: EventSchedule, at: Date): EventRun | null {
  const run = lastRun(schedule, at);
  return run && at < run.end ? run : null;
}

/** Every run that is on at some time between `from` and `to`, in order (for a calendar). */
export function runsBetween(schedule: EventSchedule, from: Date, to: Date, most = 60): EventRun[] {
  const runs: EventRun[] = [];
  const first = lastRun(schedule, from);
  if (first && first.end > from) runs.push(first);
  let after = first ?? null;
  let next = nextRun(schedule, after ? after.start : from);
  while (next && next.start < to && runs.length < most) {
    runs.push(next);
    after = next;
    next = nextRun(schedule, after.start);
  }
  return runs;
}

/** A run's leaderboard period: when it started, to the minute (`2026-11-06T18:00Z`). */
export function runPeriod(run: Pick<EventRun, 'start'>): string {
  return `${run.start.toISOString().slice(0, 16)}Z`;
}

// --- Rules -------------------------------------------------------------------------------

export const RULE_TRIGGERS = ['board_reset', 'event_end', 'times'] as const;
export type RuleTrigger = (typeof RULE_TRIGGERS)[number];
export const RULE_ACTIONS = ['achievement', 'config'] as const;
export type RuleAction = (typeof RULE_ACTIONS)[number];
export const RULE_EVERY = ['day', 'week', 'month'] as const;
export type RuleEvery = (typeof RULE_EVERY)[number];

/** The first moment of each day, week (Monday) or month, plus `minute`, at or before `at`. */
export function lastBoundary(every: RuleEvery, minute: number, at: Date): Date {
  const time = at.getTime();
  const offset = minute * MINUTE_MS;
  if (every === 'month') {
    const year = at.getUTCFullYear();
    const month = at.getUTCMonth();
    const thisMonth = Date.UTC(year, month, 1) + offset;
    return new Date(thisMonth <= time ? thisMonth : Date.UTC(year, month - 1, 1) + offset);
  }
  const cycle = (every === 'day' ? DAY_MINUTES : WEEK_MINUTES) * MINUTE_MS;
  return new Date(
    FIRST_MONDAY + Math.floor((time - FIRST_MONDAY - offset) / cycle) * cycle + offset,
  );
}

/** The next such moment after `at`. */
export function nextBoundary(every: RuleEvery, minute: number, at: Date): Date {
  const last = lastBoundary(every, minute, at);
  if (every === 'month') {
    return new Date(
      Date.UTC(last.getUTCFullYear(), last.getUTCMonth() + 1, 1) + minute * MINUTE_MS,
    );
  }
  return new Date(last.getTime() + (every === 'day' ? DAY_MINUTES : WEEK_MINUTES) * MINUTE_MS);
}

/** When a board starts fresh: its reset as a rule's `every`, or null for one that never does. */
export function resetEvery(reset: LeaderboardReset): RuleEvery | null {
  if (reset === 'daily') return 'day';
  if (reset === 'weekly') return 'week';
  if (reset === 'monthly') return 'month';
  return null;
}

/** The period of a board that ended when it started fresh at `boundary`. */
export function periodBefore(reset: LeaderboardReset, boundary: Date): string {
  return leaderboardPeriod(reset, new Date(boundary.getTime() - 1));
}

// --- Settings, as the dashboard and the CLI send them --------------------------------------

const isoTime = z.iso.datetime({ offset: true });

const eventBoardSchema = z.object({
  /** The board whose scores also count on the event's own board while it's on. */
  from: leaderboardIdSchema,
  name: z.string().trim().min(1, 'Give the board a name').max(60),
});

const eventFieldsSchema = z.object({
  name: z.string().trim().min(1, 'Give the event a name').max(60),
  repeat: z.enum(EVENT_REPEATS),
  startsAt: isoTime.nullable(),
  endsAt: isoTime.nullable(),
  onMinute: z
    .number()
    .int()
    .min(0)
    .max(WEEK_MINUTES - 1)
    .nullable(),
  offMinute: z
    .number()
    .int()
    .min(0)
    .max(WEEK_MINUTES - 1)
    .nullable(),
  /** The config values it changes while it's on. */
  config: z
    .record(configKeySchema, z.json())
    .refine((config) => Object.keys(config).length <= EVENT_CONFIG_VALUES, {
      message: `An event can change up to ${EVENT_CONFIG_VALUES} config values`,
    }),
  board: eventBoardSchema.nullable(),
  enabled: z.boolean(),
});

function checkEvent(event: z.infer<typeof eventFieldsSchema>, context: z.RefinementCtx) {
  const issue = (message: string, path: string) =>
    context.addIssue({ code: 'custom', message, path: [path] });
  const starts = event.startsAt ? Date.parse(event.startsAt) : null;
  const ends = event.endsAt ? Date.parse(event.endsAt) : null;
  if (event.repeat === 'once') {
    if (starts === null) return issue('Say when it turns on', 'startsAt');
    if (ends === null) return issue('Say when it turns off', 'endsAt');
    if (ends <= starts) return issue('It has to turn off after it turns on', 'endsAt');
    if (ends - starts > LONGEST_ONCE_MS) return issue('An event can last up to 90 days', 'endsAt');
    return;
  }
  const cycle = cycleOf(event.repeat);
  if (event.onMinute === null || event.onMinute >= cycle) {
    return issue('Say when it turns on', 'onMinute');
  }
  if (event.offMinute === null || event.offMinute >= cycle) {
    return issue('Say when it turns off', 'offMinute');
  }
  if (event.onMinute === event.offMinute) {
    return issue('It has to turn off at another time than it turns on', 'offMinute');
  }
  if (starts !== null && ends !== null && ends <= starts) {
    issue('The last day has to come after the first', 'endsAt');
  }
}

/** An event's settings (the dashboard's form, `mogzo events create`). */
export const eventSettingsSchema = eventFieldsSchema.superRefine(checkEvent);
/** Only the settings to change (the CLI's edit); checked again once merged. */
export const eventChangesSchema = eventFieldsSchema.partial();
export const newEventSchema = z.intersection(z.object({ id: eventIdSchema }), eventSettingsSchema);
export type EventSettings = z.infer<typeof eventFieldsSchema>;

export const ruleValueSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('random'),
    min: z.number().int().min(-1_000_000_000).max(1_000_000_000),
    max: z.number().int().min(-1_000_000_000).max(1_000_000_000),
  }),
  z.object({ kind: z.literal('date') }),
  z.object({ kind: z.literal('fixed'), value: z.json() }),
]);
export type RuleValue = z.infer<typeof ruleValueSchema>;

const ruleFieldsSchema = z.object({
  trigger: z.enum(RULE_TRIGGERS),
  /** board_reset: the board. */
  board: leaderboardIdSchema.nullable(),
  /** event_end: the event. */
  event: eventIdSchema.nullable(),
  /** times: how often, at `atMinute` minutes past 00:00 UTC (weeks on Mondays, months on the 1st). */
  every: z.enum(RULE_EVERY).nullable(),
  atMinute: z
    .number()
    .int()
    .min(0)
    .max(DAY_MINUTES - 1)
    .nullable(),
  action: z.enum(RULE_ACTIONS),
  /** achievement: given to this many of the best (ties included). */
  top: z.number().int().min(1).max(1000).nullable(),
  achievement: achievementIdSchema.nullable(),
  /** config: the value to set. */
  key: configKeySchema.nullable(),
  value: ruleValueSchema.nullable(),
  enabled: z.boolean(),
});

function checkRule(rule: z.infer<typeof ruleFieldsSchema>, context: z.RefinementCtx) {
  const issue = (message: string, path: string) =>
    context.addIssue({ code: 'custom', message, path: [path] });
  if (rule.trigger === 'board_reset' && !rule.board) return issue('Pick a leaderboard', 'board');
  if (rule.trigger === 'event_end' && !rule.event) return issue('Pick an event', 'event');
  if (rule.trigger === 'times') {
    if (!rule.every) return issue('Say how often', 'every');
    if (rule.atMinute === null) return issue('Say at what time', 'atMinute');
  }
  if (rule.action === 'achievement') {
    if (rule.trigger === 'times') {
      return issue(
        'At set times there’s no board to rank. Give an achievement when a leaderboard starts fresh or an event ends.',
        'action',
      );
    }
    if (!rule.top) return issue('Say how many of the top players get it', 'top');
    if (!rule.achievement) return issue('Pick an achievement', 'achievement');
  }
  if (rule.action === 'config') {
    if (!rule.key) return issue('Say which config value to set', 'key');
    if (!rule.value) return issue('Say what to set it to', 'value');
    if (rule.value.kind === 'random' && rule.value.min >= rule.value.max) {
      return issue('The lowest number has to be below the highest', 'value');
    }
  }
}

/** A rule's settings (the dashboard's form, `mogzo events rules create`). */
export const ruleSettingsSchema = ruleFieldsSchema.superRefine(checkRule);
export const ruleChangesSchema = ruleFieldsSchema.partial();
export type RuleSettings = z.infer<typeof ruleFieldsSchema>;

// --- What games see ------------------------------------------------------------------------

/** An event that is on, as `mogzo.events.active()` gives it. */
export interface LiveEvent {
  id: string;
  name: string;
  /** When this run turned on and turns off (ISO times). */
  startedAt: string;
  endsAt: string;
  /** The id of its own leaderboard, for `leaderboards.top`; null when it has none. */
  leaderboard: string | null;
  /** The config values it changes; `config.get()` already has them. */
  config: Record<string, unknown>;
}

/** The answer to `events.active`: what's on, and when that next changes (null: nothing planned). */
export interface EventsAnswer {
  events: LiveEvent[];
  changesAt: string | null;
}

/** How a run of the schedule went, for the dashboard's Recent runs. */
export const SCHEDULE_RUN_KINDS = ['on', 'off', 'rule'] as const;
export type ScheduleRunKind = (typeof SCHEDULE_RUN_KINDS)[number];

/**
 * Deleted events and rules wait in Trash this long, then go for good (Nov 3, 2026). An event
 * goes with the rules that wait for it to end, as one group, and comes back with them.
 */
export const TRASH_DAYS = 30;
/** Where a delete came from: the dashboard, the CLI, or removing it in Copy to live. */
export const TRASH_SOURCES = ['dashboard', 'cli', 'copy'] as const;
export type TrashSource = (typeof TRASH_SOURCES)[number];
